// T3-B sponsored motif (PR 4, 2026-05-07):
// sponsored_campaign の create / edit を担う Sheet UI。
// edit mode 時には motif sub-section を表示 (= spec §6.1 詳細画面)。
//
// Spec: design/sponsored-motif-spec.md §6.2 / §6.1

import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import MotifsSection from './MotifsSection';

// 月額 / 制作費の default 例 (spec §business-plan §6 を参照、UI hints のみ、editable)
const TYPE_DEFAULT_AMOUNTS: Record<string, { contract: number, production: number }> = {
    season: { contract: 150000, production: 100000 },
    series: { contract: 800000, production: 300000 },
    mega: { contract: 1500000, production: 500000 }
};

const campaignSchema = yup.object({
    sponsorId: yup
        .number()
        .typeError('スポンサーを選択してください')
        .required('スポンサーを選択してください')
        .integer()
        .min(1),
    name: yup
        .string()
        .required('キャンペーン名を入力してください')
        .max(200, '200 文字以下を入力してください'),
    campaignType: yup
        .string()
        .required('タイプを選択してください')
        .oneOf(['season', 'series', 'mega']),
    contractAmount: yup
        .number()
        .typeError('数値を入力してください')
        .required('月額を入力してください')
        .min(0)
        .integer(),
    productionFee: yup
        .number()
        .typeError('数値を入力してください')
        .required('制作費を入力してください')
        .min(0)
        .integer(),
    startAt: yup.string().required('開始日を入力してください'),
    endAt: yup.string().required('終了日を入力してください'),
    priority: yup
        .number()
        .typeError('数値を入力してください')
        .required()
        .integer(),
    collabStory: yup.string().nullable().transform((v, o) => (o === '' ? null : v)),
    culturalCredit: yup.string().nullable().transform((v, o) => (o === '' ? null : v)),
    donationPct: yup
        .number()
        .typeError('数値を入力してください')
        .required()
        .min(0)
        .max(100),
    shopUrl: yup
        .string()
        .nullable()
        .max(500)
        .transform((v, o) => (o === '' ? null : v))
        .url('URL を入力してください')
}).test(
    'period-order',
    '開始日は終了日より前である必要があります',
    function (values) {
        if (!values.startAt || !values.endAt) return true;
        if (new Date(values.startAt) >= new Date(values.endAt)) {
            return this.createError({ path: 'endAt', message: '終了日は開始日より後を指定してください' });
        }
        return true;
    }
);

type CampaignFormValues = yup.InferType<typeof campaignSchema>;

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    campaign: any | null,
    onSuccess?: () => void
}

const toIsoOrNull = (s: string | null | undefined): string | null => {
    if (!s) return null;
    try {
        return new Date(s).toISOString();
    } catch {
        return null;
    }
};

const toDateInput = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    } catch {
        return '';
    }
};

export default observer(function SponsoredCampaignFormSheet({ open, onOpenChange, mode, campaign, onSuccess }: Props) {
    const {
        sponsorStore: { sponsors },
        sponsoredCampaignStore: { create: createCampaign, update: updateCampaign }
    } = useStore();

    const form = useForm<CampaignFormValues>({
        resolver: yupResolver(campaignSchema) as any,
        mode: 'onSubmit',
        defaultValues: {
            sponsorId: undefined as any,
            name: '',
            campaignType: 'season',
            contractAmount: TYPE_DEFAULT_AMOUNTS.season.contract,
            productionFee: TYPE_DEFAULT_AMOUNTS.season.production,
            startAt: '',
            endAt: '',
            priority: 100,
            collabStory: '',
            culturalCredit: '',
            donationPct: 0,
            shopUrl: ''
        }
    });

    useEffect(() => {
        if (open) {
            form.reset(
                mode === 'edit' && campaign ?
                    {
                        sponsorId: campaign.sponsor_id,
                        name: campaign.name ?? '',
                        campaignType: campaign.campaign_type ?? 'season',
                        contractAmount: Number(campaign.contract_amount ?? 0),
                        productionFee: Number(campaign.production_fee ?? 0),
                        startAt: toDateInput(campaign.start_at),
                        endAt: toDateInput(campaign.end_at),
                        priority: Number(campaign.priority ?? 100),
                        collabStory: campaign.collab_story ?? '',
                        culturalCredit: campaign.cultural_credit ?? '',
                        donationPct: Number(campaign.donation_pct ?? 0),
                        shopUrl: campaign.shop_url ?? ''
                    } :
                    {
                        sponsorId: undefined as any,
                        name: '',
                        campaignType: 'season',
                        contractAmount: TYPE_DEFAULT_AMOUNTS.season.contract,
                        productionFee: TYPE_DEFAULT_AMOUNTS.season.production,
                        startAt: '',
                        endAt: '',
                        priority: 100,
                        collabStory: '',
                        culturalCredit: '',
                        donationPct: 0,
                        shopUrl: ''
                    }
            );
        }
    }, [open, mode, campaign]);

    const onTypeChange = (type: string) => {
        form.setValue('campaignType', type as any);
        // create mode 時のみ default 金額を提案 (edit mode は既存値を尊重)
        if (mode === 'create' && TYPE_DEFAULT_AMOUNTS[type]) {
            form.setValue('contractAmount', TYPE_DEFAULT_AMOUNTS[type].contract);
            form.setValue('productionFee', TYPE_DEFAULT_AMOUNTS[type].production);
        }
    };

    const onSubmit = async (data: CampaignFormValues) => {
        const payload = {
            sponsor_id: data.sponsorId,
            name: data.name,
            campaign_type: data.campaignType,
            contract_amount: data.contractAmount,
            production_fee: data.productionFee,
            start_at: toIsoOrNull(data.startAt),
            end_at: toIsoOrNull(data.endAt),
            priority: data.priority,
            collab_story: data.collabStory ?? null,
            cultural_credit: data.culturalCredit ?? null,
            donation_pct: data.donationPct,
            shop_url: data.shopUrl ?? null
        };
        const res = mode === 'create' ?
            await flowResult(createCampaign(payload)) :
            await flowResult(updateCampaign({ id: campaign.id, data: payload }));

        if (res?.ok) {
            toastify('success', mode === 'create' ? '作成しました' : '更新しました');
            onOpenChange(false);
            onSuccess?.();
        } else {
            toastify('error', res?.message || '保存に失敗しました');
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[800px] sm:max-w-[800px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'キャンペーン新規作成' : 'キャンペーン編集'}</SheetTitle>
                    <SheetDescription>
                        コラボ motif の配布期間と契約条件を登録します。
                    </SheetDescription>
                </SheetHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
                        <FormField
                            control={form.control}
                            name='sponsorId'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        スポンサー
                                        <span className='text-red-500'>*</span>
                                    </FormLabel>
                                    <Select
                                        value={field.value ? String(field.value) : ''}
                                        onValueChange={v => field.onChange(Number(v))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder='スポンサーを選択' />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sponsors.map(sp => (
                                                <SelectItem key={sp.id} value={String(sp.id)}>
                                                    {sp.name}
                                                    {sp.industry ? ` (${sp.industry})` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        キャンペーン名
                                        <span className='text-red-500'>*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder='例: 5 月限定 オリオン獅子' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='campaignType'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            タイプ
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <Select value={field.value} onValueChange={onTypeChange}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='season'>シーズン (1 motif × 1 ヶ月)</SelectItem>
                                                <SelectItem value='series'>シリーズ (5 motifs × 3 ヶ月)</SelectItem>
                                                <SelectItem value='mega'>メガ (25 セル × 1 ヶ月)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='priority'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>優先度</FormLabel>
                                        <FormControl>
                                            <Input type='number' {...field} value={field.value ?? 100} />
                                        </FormControl>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>同時 active 時の優先度 (大きい順)</p>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='startAt'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            開始日
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input type='date' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='endAt'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            終了日
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input type='date' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='contractAmount'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            月額 (円)
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input type='number' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='productionFee'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            制作費 (円、one-time)
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input type='number' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name='collabStory'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>コラボストーリー (Layer 4 全画面)</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            {...field}
                                            value={field.value ?? ''}
                                            rows={4}
                                            placeholder='200-500 字推奨。未入力時は Layer 4 自体を表示しない。'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='culturalCredit'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>監修クレジット</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            value={field.value ?? ''}
                                            placeholder='例: 紅型師: ◯◯ (文化モチーフ時に必須)'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='donationPct'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>文化保存基金 寄付率 (%)</FormLabel>
                                        <FormControl>
                                            <Input type='number' step='0.01' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>
                                            このキャンペーンの絵柄が【スポンサー排出】で出た時に適用。
                                            ※ 現在は保存のみ・寄付集計は Phase 2 で対応予定。通常排出は絵柄セット側の寄付率を使用。
                                        </p>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='shopUrl'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>ショップ URL (Layer 4 CTA)</FormLabel>
                                        <FormControl>
                                            <Input {...field} value={field.value ?? ''} placeholder='https://...' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <SheetFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                キャンセル
                            </Button>
                            <Button type='submit' variant='active'>
                                {mode === 'create' ? '作成' : '更新'}
                            </Button>
                        </SheetFooter>
                    </form>
                </Form>

                {mode === 'edit' && campaign && (
                    <div className='mt-8 pt-6 border-t border-gray-200'>
                        <MotifsSection campaignId={campaign.id} campaignStatus={campaign.status} />
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
});
